import WaCheckbox from "@awesome.me/webawesome-pro/dist/react/checkbox/index.js";
import WaInput from "@awesome.me/webawesome-pro/dist/react/input/index.js";
import React, { useLayoutEffect, useRef } from "react";

type Props = {
  leaveNow: boolean;
  travelTime: string; // value for datetime-local: "YYYY-MM-DDTHH:mm"
  onLeaveNowChange: (v: boolean) => void;
  ontravelTimeChange: (v: string) => void;

  disabled?: boolean;

  minTravelTime?: string;
  travelTimeErrorText?: string | null;

  /**
   * Optional: bump this when you switch screens to force a resync of the web component
   * (useful when toggling mobileMode input/result).
   */
  syncKey?: string | number;
};

export const DepartureSelector: React.FC<Props> = ({
  leaveNow,
  travelTime,
  onLeaveNowChange,
  ontravelTimeChange,
  disabled,
  minTravelTime,
  travelTimeErrorText,
  syncKey,
}) => {
  const checkboxRef = useRef<any>(null);

  // IMPORTANT: web component sometimes doesn't reflect controlled prop on first paint.
  // Force the underlying element's property to match.
  useLayoutEffect(() => {
    const el = checkboxRef.current;
    if (!el) return;

    try {
      // property
      el.checked = !!leaveNow;

      // attribute (belt + braces)
      if (leaveNow) el.setAttribute?.("checked", "");
      else el.removeAttribute?.("checked");
    } catch {
      // ignore
    }
  }, [leaveNow, syncKey]);

  return (
    <div className="wa-stack wa-gap-s">
      <div className="wa-stack wa-gap-l">
        <span className="wa-heading-m wa-color-neutral-600" style={{ pointerEvents: "none" }}>
          Departure
        </span>

        <div
          className="wa-stack wa-gap-2xs"
          style={{
            padding: "8px 10px",
            borderRadius: 10,
            background: "rgba(15,23,42,0.03)",
            border: "1px solid rgba(15,23,42,0.08)",
          }}
        >
          <WaCheckbox
            ref={checkboxRef}
            checked={leaveNow}
            disabled={disabled}
            onChange={(e: any) => {
              const next =
                typeof e?.detail?.checked === "boolean"
                  ? e.detail.checked
                  : Boolean(e?.target?.checked);

              onLeaveNowChange(next);
            }}
          >
            <span style={{ fontWeight: 600 }}>Leave now</span>
          </WaCheckbox>

          <span className="wa-caption-s wa-color-neutral-500" style={{ pointerEvents: "none" }}>
            Uses live traffic and the current tariff to pick the best route.
          </span>
        </div>

        {!leaveNow && (
          <div className="wa-stack wa-gap-2xs">
            <WaInput
              type="datetime-local"
              label="Planned departure time"
              data-wa-datepicker
              data-invalid={travelTimeErrorText ? "true" : "false"}
              value={travelTime}
              min={minTravelTime}
              disabled={disabled}
              onInput={(e: any) => ontravelTimeChange(String(e?.target?.value ?? ""))}
            />

            {travelTimeErrorText ? (
              <span
                className="wa-caption-s"
                style={{ lineHeight: 1.2, color: "rgba(220,38,38,0.92)", pointerEvents: "none" }}
              >
                {travelTimeErrorText}
              </span>
            ) : (
              <span className="wa-caption-s wa-color-neutral-500" style={{ pointerEvents: "none" }}>
                Uses the tariff for the selected time and does not consider live traffic.
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
